import {css, StyleSheet} from 'aphrodite';
import {print as astToString} from 'query-api-graphql';
import React, {useCallback, useState} from 'react';

import buildQueryApiQueryAst from 'analysis-tool/data-frame-creation/schemas/buildQueryApiQueryAst';
// eslint-disable-next-line import-access/jsdoc
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {MAX_NUM_QUERY_API_FIELDS} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationConstants';
import {sum} from 'BenchlingUtil';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import LookUpSchemaSelectorStepper from './LookUpSchemaSelectorStepper';

const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};

export default function LookUpFieldEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  const [queryFormState, setQueryFormState] = useState<FormState>(DEFAULT_QUERY_FORM_STATE);
  const queryApiIntrospection = useQueryApiIntrospection();

  const handleChangeQueryFormState = useCallback(
    (newFormState: FormState) => {
      const totalFields =
        newFormState.entityListingFieldName == null
          ? 0
          : countSelectedFields(newFormState.entityFieldSelections.selections) +
            sum(
              newFormState.relatedResultsFormStates.map((resultFormState) =>
                countSelectedFields(resultFormState.backlinkSelections.selections)
              )
            );
      if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
        notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
          duration: 4,
        });
      } else {
        setQueryFormState(newFormState);
      }
      const queryAst = buildQueryApiQueryAst(newFormState);
      const queryString = queryAst != null ? astToString(queryAst) : null;
      const schemaId =
        queryApiIntrospection.status !== QueryResultType.ERROR &&
        queryApiIntrospection.status !== QueryResultType.PENDING &&
        newFormState.entityListingFieldName != null
          ? queryApiIntrospection.getQueryFieldSchemaId(newFormState.entityListingFieldName)
          : null;

      // TODO: Set two fields on a config at once
      const configWithQuerySet = setValueInConfig(config, ['query'], queryString);
      onChangeConfig(configWithQuerySet, ['schemaId'], schemaId);
    },
    [config, onChangeConfig, queryApiIntrospection, setQueryFormState]
  );

  return (
    <div className={css(styles.bannerWrapper)}>
      {queryApiIntrospection.status === QueryResultType.ERROR ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />
      ) : queryApiIntrospection.status === QueryResultType.PENDING ? (
        <CenteredHorizontalThrobber />
      ) : (
        <LookUpSchemaSelectorStepper
          queryApiIntrospection={queryApiIntrospection}
          queryFormState={queryFormState}
          setQueryFormState={handleChangeQueryFormState}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  bannerWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
});
